import { useEffect, useState, useCallback } from "react";
import { useApp } from "@/context/AppContext";
import { Header } from "@/components/Header";
import { StatusBadge } from "@/components/StatusBadge";
import client from "@/api";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, CheckCircle2, XCircle, RefreshCw, BookOpen } from "lucide-react";

export default function Quiz() {
  const { t, lang } = useApp();
  const { chapterId } = useParams();
  const navigate = useNavigate();
  const [attempt, setAttempt] = useState(null);
  const [answers, setAnswers] = useState({});
  const [idx, setIdx] = useState(0);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const start = useCallback(async () => {
    setResult(null); setAnswers({}); setIdx(0); setAttempt(null);
    try {
      const { data } = await client.post(`/chapters/${chapterId}/quiz/start?lang=${lang}`);
      setAttempt(data);
    } catch (e) { toast.error(e.response?.data?.detail || t("error")); }
  }, [chapterId, lang]);

  useEffect(() => { start(); }, [start]);

  const submit = async () => {
    const unanswered = attempt.questions.filter((q) => !answers[q.question_id]).length;
    if (unanswered > 0 && !window.confirm(`${unanswered} ${t("unanswered_count")}. ${t("unanswered_warn")}`)) return;
    setBusy(true);
    try {
      const { data } = await client.post(`/quiz/attempts/${attempt.attempt_id}/submit`, { answers });
      setResult(data);
      window.scrollTo(0, 0);
    } catch (e) { toast.error(e.response?.data?.detail || t("error")); }
    finally { setBusy(false); }
  };

  if (!attempt && !result) return <Shell><div className="animate-pulse h-72 bg-[#1a1a1a] rounded-2xl" /></Shell>;

  if (result) return <Result result={result} chapterId={chapterId} onRetry={start} />;

  const q = attempt.questions[idx];
  const answeredCount = Object.values(answers).filter(Boolean).length;

  return (
    <Shell>
      <div className="flex items-center justify-between mb-6">
        <button data-testid="quiz-back-btn" onClick={() => navigate(`/chapters/${chapterId}`)}
          className="focusable flex items-center gap-1 text-sm text-[#C9CDD3] hover:text-[#D21F2A] transition-colors">
          <ArrowLeft className="w-4 h-4" /> {t("back_to_chapters")}
        </button>
        <span className="text-sm text-[#C9CDD3]">{answeredCount}/{attempt.total}</span>
      </div>

      <div className="w-full h-1.5 bg-[#1a1a1a] rounded-full mb-8 overflow-hidden">
        <div className="h-full bg-[#D21F2A] transition-all" style={{ width: `${((idx + 1) / attempt.total) * 100}%` }} />
      </div>

      <div className="card-dark rounded-2xl p-6 sm:p-8 fade-up" data-testid="quiz-question">
        <div className="text-xs uppercase tracking-wider text-[#D21F2A] mb-3">{t("question")} {idx + 1} {t("of")} {attempt.total}</div>
        <h2 className="font-serif text-xl sm:text-2xl text-[#FFFFFF] mb-6">{q.prompt}</h2>
        <div className="space-y-3" role="radiogroup">
          {q.options.map((o) => {
            const sel = answers[q.question_id] === o.option_id;
            return (
              <button key={o.option_id} role="radio" aria-checked={sel} data-testid={`quiz-option-${o.option_id}`}
                onClick={() => setAnswers((a) => ({ ...a, [q.question_id]: o.option_id }))}
                className={`focusable w-full text-left px-5 py-4 rounded-xl border transition-colors flex items-center gap-3 ${sel ? "border-[#D21F2A] bg-[#D21F2A]/10" : "border-[#D21F2A]/15 bg-[#141414] hover:border-[#D21F2A]/40"}`}>
                <span className={`w-5 h-5 rounded-full border-2 shrink-0 flex items-center justify-center ${sel ? "border-[#D21F2A]" : "border-[#9AA0A6]"}`}>
                  {sel && <span className="w-2.5 h-2.5 rounded-full bg-[#D21F2A]" />}
                </span>
                <span className="text-[#FFFFFF]">{o.text}</span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex items-center justify-between mt-6">
        <button data-testid="quiz-prev-btn" disabled={idx === 0} onClick={() => setIdx((i) => i - 1)}
          className="focusable px-4 py-2.5 rounded-lg border border-[#D21F2A]/20 text-[#FFFFFF] disabled:opacity-40 flex items-center gap-1">
          <ArrowLeft className="w-4 h-4" /> {t("prev")}
        </button>
        {idx < attempt.total - 1 ? (
          <button data-testid="quiz-next-btn" onClick={() => setIdx((i) => i + 1)}
            className="btn-gold focusable px-5 py-2.5 rounded-lg flex items-center gap-1">{t("next")} <ArrowRight className="w-4 h-4" /></button>
        ) : (
          <button data-testid="quiz-submit-btn" disabled={busy} onClick={submit}
            className="btn-gold focusable px-6 py-2.5 rounded-lg">{busy ? t("loading") : t("submit_quiz")}</button>
        )}
      </div>
      <p className="text-xs text-[#9AA0A6] mt-4 text-center">{t("unanswered_warn")}</p>
    </Shell>
  );
}

function Result({ result, chapterId, onRetry }) {
  const { t } = useApp();
  const navigate = useNavigate();
  return (
    <Shell>
      <div className="card-dark rounded-2xl p-8 text-center fade-up" data-testid="quiz-result">
        <div className="text-xs uppercase tracking-wider text-[#D21F2A]">{t("result")}</div>
        <div className="font-serif text-6xl text-[#FFFFFF] my-3">{result.percent}%</div>
        <p className="text-[#C9CDD3]">{result.correct_count} / {result.total} {t("correct_answers")}</p>
        {result.below_hint && <p className="text-[#FDE047] text-sm mt-3">{t("below_hint", { n: result.pass_hint })}</p>}
        <div className="flex flex-wrap gap-3 justify-center mt-6">
          <button data-testid="retry-quiz-btn" onClick={onRetry} className="btn-gold focusable px-5 py-2.5 rounded-lg flex items-center gap-2"><RefreshCw className="w-4 h-4" /> {t("retry_quiz")}</button>
          <button data-testid="repeat-chapter-btn" onClick={() => navigate(`/chapters/${chapterId}`)} className="focusable px-5 py-2.5 rounded-lg border border-[#D21F2A]/20 text-[#FFFFFF] flex items-center gap-2"><BookOpen className="w-4 h-4" /> {t("repeat_chapter")}</button>
        </div>
      </div>

      {result.recommendations?.length > 0 && (
        <div className="mt-6 card-dark rounded-2xl p-6" data-testid="recommendations">
          <h3 className="font-serif text-xl text-[#FFFFFF] mb-3">{t("recommendation")}</h3>
          <ul className="space-y-2">
            {result.recommendations.map((r, i) => (
              <li key={i} className="text-sm text-[#C9CDD3] flex items-start gap-2">
                <span className="text-[#D21F2A]">→</span>
                {t("rec_text", { chapter: r.chapter_title, section: r.section_heading || "—" })}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="mt-6 space-y-4">
        {result.details.map((d, i) => (
          <div key={i} className="card-dark rounded-2xl p-5" data-testid={`result-detail-${i}`}>
            <div className="flex items-start gap-2 mb-3">
              {d.is_correct ? <CheckCircle2 className="w-5 h-5 text-[#6EE7B7] shrink-0 mt-0.5" /> : <XCircle className="w-5 h-5 text-[#FCA5A5] shrink-0 mt-0.5" />}
              <p className="text-[#FFFFFF] font-medium">{d.prompt}</p>
            </div>
            <div className="space-y-1.5 pl-7 text-sm">
              {d.options.map((o) => {
                const isCorrect = o.option_id === d.correct_option_id;
                const isGiven = o.option_id === d.given;
                return (
                  <div key={o.option_id} className={`px-3 py-1.5 rounded-lg border flex items-center gap-2 ${isCorrect ? "border-[#276239] bg-[#142E1F] text-[#6EE7B7]" : isGiven ? "border-[#7F1D1D] bg-[#3B1414] text-[#FCA5A5]" : "border-transparent text-[#C9CDD3]"}`}>
                    {isCorrect && <CheckCircle2 className="w-3.5 h-3.5" />}
                    {isGiven && !isCorrect && <XCircle className="w-3.5 h-3.5" />}
                    <span>{o.text}</span>
                    {isGiven && <span className="text-xs opacity-70">({t("your_answer")})</span>}
                  </div>
                );
              })}
              {!d.given && <p className="text-xs text-[#FDE047]">{t("not_answered")}</p>}
              <p className="text-[#C9CDD3] pt-2"><span className="text-[#D21F2A]">{t("explanation")}: </span>{d.explanation}</p>
              {d.section_heading && <p className="text-xs text-[#9AA0A6]">{t("source_pdf")} {d.source_page} · {d.section_heading}</p>}
            </div>
          </div>
        ))}
      </div>
    </Shell>
  );
}

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-[#0d0d0d] bg-grain">
      <Header />
      <main className="max-w-3xl mx-auto px-4 sm:px-8 py-10">{children}</main>
    </div>
  );
}
